import {
  RadioGroup as MuiRadioGroup,
  RadioGroupProps as MuiRadioGroupProps,
  FormLabel,
  FormHelperText,
  Grid,
} from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { DesignToken } from '~c/components/base/core';

type CustomMuiRadioGroupProps = Omit<MuiRadioGroupProps, 'title'>;

export interface RadioGroupProps extends CustomMuiRadioGroupProps {
  className?: string;
  /**
   * Title of radio group
   */
  title?: React.ReactNode;
  /**
   * If `true`, the label is displayed in an error state.
   * @default false
   */
  error?: boolean;
  /**
   * The helper text content.
   */
  helperText?: React.ReactNode;
  /**
   * If `true`, uses simplified layout without Grid container.
   * Intended for use within QuestionLayout component.
   * @default false
   */
  simpleLayout?: boolean;
}

const RadioGroupImpl = React.forwardRef<HTMLLabelElement, RadioGroupProps>(
  (
    {
      className,
      title,
      error,
      helperText,
      defaultValue,
      simpleLayout = false,
      ...props
    },
    ref
  ) => {
    /**
     * This is a workaround to solve the problem that MUI raises
     * when defaultValue state is changed for uncontrolled component
     * -  component is changing the default value state of an uncontrolled
     *    RadioGroup after being initialized.
     */
    const _defaultValue = React.useRef(defaultValue);

    // Simple layout for use within QuestionLayout component
    if (simpleLayout) {
      return (
        <div className={className}>
          {title && (
            <FormLabel component="legend" error={error}>
              {title}
            </FormLabel>
          )}
          <MuiRadioGroup
            ref={ref}
            defaultValue={_defaultValue.current}
            {...props}
          />
          {helperText && (
            <FormHelperText error={error}>{helperText}</FormHelperText>
          )}
        </div>
      );
    }

    // Original Grid-based layout for backward compatibility
    return (
      <Grid container className={className}>
        <Grid item xs={12} md={6}>
          <FormLabel component="legend" error={error} sx={{ width: '745px' }}>
            {title}
          </FormLabel>
        </Grid>
        <Grid item xs={12} md={6}>
          <MuiRadioGroup
            row
            ref={ref}
            defaultValue={_defaultValue.current}
            {...props}
          />
          <FormHelperText error={error}>{helperText}</FormHelperText>
        </Grid>
      </Grid>
    );
  }
);

export const RadioGroup = muiStyled(RadioGroupImpl)<RadioGroupProps>(({
  theme,
  error,
  simpleLayout,
}) => {
  return css`
    ${!simpleLayout && `padding: 16px 16px 24px;`}
    color: ${DesignToken.form.labelText};

    legend.Mui-focused {
      /* override focus color on title */
      color: ${error ? theme.palette.error.main : theme.palette.text.primary};
    }

    ${!simpleLayout &&
    `
      .MuiFormGroup-root {
        margin-left: 9px;
      }
    `}

    .MuiFormHelperText-root {
      margin-left: 0;
      ${simpleLayout && `margin-top: ${theme.spacing(1)};`}
    }

    /* Simple layout styles for use with QuestionLayout */
    ${simpleLayout &&
    `
      & > .MuiFormLabel-root {
        margin-bottom: ${theme.spacing(1)};
        color: ${DesignToken.form.labelText};
      }

      .MuiFormGroup-root {
        gap: ${theme.spacing(1)};
      }

      .MuiFormControlLabel-root {
        margin-bottom: ${theme.spacing(1)};
        margin-right: 0;
        align-items: flex-start;
      }
    `}
  `;
});

export default RadioGroup;
